import Config from "@/config/config.js";
import AsyncUtils from "@/utils/asyncUtils.js";
import Services, { handleError } from "./index.service";
import { getSession } from "@/hooks/useSession.js";
const { OshimedBackend, OshimedAPI } = Services;
import { v4 as uuidv4 } from "uuid";

/**
 * POST /upload-signed-consent – Upload du consentement signé d’un patient.
 * @param {object} data – Contient patientId, studyId, siteIcfId, fileBlob, fileHash, consentId
 * @returns {Promise<object>} Résultat de l’upload ou erreur normalisée.
 */
const savePatientSignedConsent = async (data) => {
  console.log(
    "=== Service: savePatientSignedConsent called === study.service.js === key: 615061 ==="
  );
  console.dir(data, { depth: null, colors: true });
  console.log("=================================");

  try {
    const { studyId } = data;
    const url = `/participant/${studyId}/patient-consent`;

    const response = await OshimedBackend.patch(url, data);

    console.log("=== API response === study.service.js === key: 615062 ===");
    console.dir(response.data, { depth: null, colors: true });
    console.log("=================================");

    return response.data;
  } catch (error) {
    console.error("Service error in savePatientSignedConsent:", error);
    console.error("Error details:", error.response?.data || error.message);
    return handleError(error, 615063);
  }
};

const uploadPatientConsent = async (formData, xApiKey) => {
  const session = getSession();
  try {
    const response = await OshimedAPI.patch(
      "/participant/consent-document",
      formData,
      {
        headers: {
          Authorization: `Bearer ${session?.apiToken}`,
          ...(xApiKey ? { "x-api-key": xApiKey } : {}),
          "Content-Type": "multipart/form-data",
          // 'Content-Type' est défini automatiquement par Axios pour FormData
        },
      }
    );

    console.log("=== response === participant.service.js === key: 334163 ===");
    console.dir(response, { depth: null, colors: true });
    console.log("=================================");

    if (!response.data) {
      return {
        success: false,
        message: "Empty response while patching protocol version",
        fromError: response,
        errorCode: "patch-protocol-version-empty",
        errorKey: 615942,
      };
    }
    return response.data;
  } catch (error) {
    console.log(
      "=== error === participant.service.js (uploadPatientConsent) === key: 615942 ==="
    );
    console.dir(error, { depth: null, colors: true });
    console.log("=================================");
    return handleError(error, 615942);
  }
};

const uploadInvestigatorConfirmConsent = async (formData, xApiKey) => {
  const session = getSession();
  try {
    const response = await OshimedAPI.patch(
      "/participant/confirm-consent",
      formData,
      {
        headers: {
          Authorization: `Bearer ${session?.apiToken}`,
          ...(xApiKey ? { "x-api-key": xApiKey } : {}),
          "Content-Type": "multipart/form-data",
          // 'Content-Type' est défini automatiquement par Axios pour FormData
        },
      }
    );

    console.log("=== response === participant.service.js === key: 694847 ===");
    console.dir(response, { depth: null, colors: true });
    console.log("=================================");

    if (!response.data) {
      return {
        success: false,
        message: "Error while uploading investigator confirm consent",
        fromError: response,
        errorCode: "upload-investigator-confirm-consent-error",
        errorKey: 993149,
      };
    }
    return response.data;
  } catch (error) {
    console.log(
      "=== error === participant.service.js (uploadInvestigatorConfirmConsent) === key: 619225 ==="
    );
    console.dir(error, { depth: null, colors: true });
    console.log("=================================");
    return handleError(error, 711385);
  }
};

/**
 * PUT /:studyId/patient-consent/revoke – Révocation d’un consentement signé.
 * @param {object} params – Contient consent_id, participant_id?, site_icf_id?, studyId
 * @returns {Promise<object>} – Résultat de la révocation ou erreur normalisée.
 */

const revokePatientConsent = async (params) => {
  console.log(
    "=== revokePatientConsent === participant.service.js === key: 408416 ==="
  );
  console.dir(params, { depth: null, colors: true });

  try {
    const { studyId, ...body } = params;
    const url = `/participant/${studyId}/patient-consent/revoke`;

    const response = await OshimedBackend.put(url, body);

    console.log(
      "=== Api response === participant.service.js === key: 475179 ==="
    );
    console.dir(response.data, { depth: null, colors: true });
    console.log("=================================");

    return response.data;
  } catch (error) {
    console.error("Service error in revokePatientConsent:", error);
    console.error("Error details:", error.response?.data || error.message);
    return handleError(error, 491286);
  }
};

/**
 * POST /investigator-consent/upload – saves the investigator’s signature for the latest consent.
 * @param {object} data
 * @param {string} data.studyId
 * @param {string} data.participant_id
 * @param {string} data.site_icf_id
 * @param {string} data.investigator_id
 * @param {string} data.investigator_file_hash
 * @returns {Promise<object>} normalized API result
 */
const saveInvestigatorConfirmConsent = async (data) => {
  console.log(
    "=== Service: saveInvestigatorConfirmConsent called === participants.service.js === key: 728401 ==="
  );
  console.dir(data, { depth: null, colors: true });
  console.log("=================================");

  try {
    const { studyId } = data;
    const url = `/participant/${studyId}/investigator-consent`;

    const response = await OshimedBackend.patch(url, data);

    console.log(
      "=== API response === participants.service.js === key: 728402 ==="
    );
    console.dir(response.data, { depth: null, colors: true });
    console.log("=================================");

    return response.data;
  } catch (error) {
    console.error("Service error in saveInvestigatorConfirmConsent:", error);
    console.error("Error details:", error.response?.data || error.message);
    return handleError(error, 728403);
  }
};

const updateParticipantUserInfo = async ({
  studyId,
  participantId,
  updates,
  payload,
}) => {
  console.log(
    "=== Service: updateParticipantUserInfo === participants.service.js ==="
  );
  console.dir(
    { studyId, participantId, updates, payload },
    { depth: null, colors: true }
  );

  try {
    const url = `/participant/${studyId}/participant/${participantId}/user`;
    const body = updates ?? payload ?? {}; // ✅ fallback
    const response = await OshimedBackend.patch(url, body);

    console.log("=== API response === participants.service.js ===");
    console.dir(response?.data, { depth: null, colors: true });

    return response?.data;
  } catch (error) {
    console.error("Service error in updateParticipantUserInfo:", error);
    console.error("Error details:", error.response?.data || error.message);
    return handleError(error, 901300);
  }
};

const getPatientLastConsent = async (participantId) => {
  console.log(
    "=== Service: getPatientLastConsent === participants.service.js ==="
  );
  console.dir({ participantId }, { depth: null, colors: true });

  try {
    const url = `/participant/${participantId}/last-consent`;
    const response = await OshimedBackend.get(url);

    if (!response?.data) {
      return {
        success: false,
        message: "Empty response while getting last consent",
        fromError: response,
        errorCode: "get-last-consent-empty",
        errorKey: 812906,
      };
    }

    if (response?.data?.success === false) {
      return response?.data;
    }

    return response?.data;
  } catch (error) {
    console.log("=== error === participant.service.js === key: 927396 ===");
    console.dir(error, { depth: null, colors: true });
    console.log("=================================");
    return handleError(error, 341331);
  }
};

const getMilestonesForParticipant = async (participantId) => {
  console.log('=== participantId === participant.service.js === key: 833557 ===');
  console.dir(participantId, { depth: null, colors: true })
  console.log('=================================');

  try {
    const url = `/participant/${participantId}/milestones`;
    const response = await OshimedBackend.get(url);

    if (!response?.data) {
      return {
        success: false,
        message: "Empty response while getting milestones",
        fromError: response,
        errorCode: "get-milestones-empty",
        errorKey: 176827,
      };
    }

    if (response?.data?.success === false) {
      return response?.data;
    }

    return response?.data;
  } catch (error) {
    console.log('=== error === participant.service.js === key: 947706 ===');
    console.dir(error, { depth: null, colors: true })
    console.log('=================================');
    return handleError(error, 130452);
  }
};

const addParticipantMilestone = async (params,xApiKeys) => {
  console.log('=== params === participant.service.js === key: 466377 ===');
  console.dir(params, { depth: null, colors: true })
  console.log('=================================');

  const session = getSession();

  let apiResp;
  try {
    apiResp = await OshimedAPI.patch("/participant/add-milestone", {...params}, {
      headers: {
        Authorization: `Bearer ${session.apiToken}`,
        "x-api-key": xApiKeys[0],
      },
    });
  } catch (error) {
    console.log('=== error === participant.service.js === key: 638704 ===');
    console.dir(error, { depth: null, colors: true })
    console.log('=================================');
    //return handleError(error, 568838);
  }

  console.log('=== apiResp === participant.service.js === key: 741956 ===');
  console.dir(apiResp, { depth: null, colors: true })
  console.log('=================================');

  try {
    apiResp = await OshimedAPI.patch("/participant/check-milestone", {...params}, {
      headers: {
        Authorization: `Bearer ${session.apiToken}`,
        "x-api-key": xApiKeys[1],
      },
    });
  } catch (error) {
    console.log('=== error === participant.service.js === key: 650137 ===');
    console.dir(error, { depth: null, colors: true })
    console.log('=================================');
    //return handleError(error, 568838);
  }

  console.log('=== apiResp 2 === participant.service.js === key: 000858 ===');
  console.dir(apiResp, { depth: null, colors: true })
  console.log('=================================');

 params.participantMilestoneId = apiResp?.data?.milestoneId || uuidv4();

  try {
    const url = `/participant/milestone`;
    const response = await OshimedBackend.post(url, params);

    if (!response?.data) {
      return {
        success: false,
        message: "Empty response while adding participant milestone",
        fromError: response,
        errorCode: "add-participant-milestone-empty",
        errorKey: 486451,
      };
    }

    if (response?.data?.success === false) {
      return response?.data;
    }

    return response?.data;
  } catch (error) {
    console.log('=== error === participant.service.js === key: 770099 ===');
    console.dir(error, { depth: null, colors: true })
    console.log('=================================');
    return handleError(error, 130452);
  }
};

const getPatientConsentDocument = async (participantId, studyId, xApiKey) => {
  const session = getSession();
  try {
    const response = await OshimedAPI.get(
      `/participant/consent-document`, 
      {
        headers: {
          Authorization: `Bearer ${session?.apiToken}`,
          ...(xApiKey ? { "x-api-key": xApiKey } : {}),
        },
        params: {
          studyId: studyId,            
          participantId: participantId
        },
        responseType: 'blob',
      }
    );

    return {
      success: true,
      blob: response.data,
    };
  } catch (error) {
    return handleError(error, 778811);
  }
};

const ParticipantService = {
  uploadPatientConsent,
  savePatientSignedConsent,
  revokePatientConsent,
  updateParticipantUserInfo,
  uploadInvestigatorConfirmConsent,
  saveInvestigatorConfirmConsent,
  getPatientLastConsent,
  addParticipantMilestone,
  getMilestonesForParticipant,
  getPatientConsentDocument,
};

export default ParticipantService;
